import { Link } from "react-router-dom";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocalePath } from "../hooks/useLocalePath";
import { useAnalytics, EVENTS } from "../hooks/useAnalytics";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import {
  Globe2, Calendar, Camera, ClipboardList, AlertTriangle,
  Search, CheckCircle2, ShieldCheck, BookOpen, Clock,
  Compass, Wand2, ChevronRight,
} from "lucide-react";

const guideLinks = [
  { path: "/green-card-lottery", icon: Globe2, color: "bg-teal-50 text-teal-700 border-teal-200" },
  { path: "/dv-lottery-dates", icon: Calendar, color: "bg-sky-50 text-sky-700 border-sky-200" },
  { path: "/dv-lottery-photo-requirements", icon: Camera, color: "bg-violet-50 text-violet-700 border-violet-200" },
  { path: "/how-to-fill-dv-lottery-form", icon: ClipboardList, color: "bg-amber-50 text-amber-700 border-amber-200" },
  { path: "/dv-lottery-common-mistakes", icon: AlertTriangle, color: "bg-rose-50 text-rose-700 border-rose-200" },
  { path: "/how-to-check-dv-lottery-results", icon: Search, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { path: "/what-to-do-after-winning-dv-lottery", icon: CheckCircle2, color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  { path: "/dv-lottery-eligibility", icon: ShieldCheck, color: "bg-cyan-50 text-cyan-700 border-cyan-200" },
];

const toolLinks = [
  { path: "/gc", icon: BookOpen },
  { path: "/gc/green-card-timeline", icon: Clock },
  { path: "/auth?mode=register", icon: Compass },
  { path: "/auth?mode=register&redirect=%2Fchecklists", icon: ClipboardList },
];

export function DVGuidesNav({ currentPath }) {
  const { t } = useTranslation();
  const lp = useLocalePath();
  const { trackEvent } = useAnalytics();
  const guides = t("dvNav.guides", { returnObjects: true });
  const tools = t("dvNav.tools", { returnObjects: true });

  const handleToolClick = useCallback((i) => {
    const events = [null, EVENTS.TIMELINE_CALCULATOR, EVENTS.START_FREE_GUIDE, EVENTS.GENERATE_CHECKLIST];
    if (events[i]) trackEvent(events[i], { source: "dv_guides_nav" });
  }, [trackEvent]);

  return (
    <section className="py-14 md:py-20 bg-white" data-testid="dv-guides-nav">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvNav.label")}</p>
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvNav.title")}</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.isArray(guides) && guides.map((guide, i) => {
            const link = guideLinks[i];
            if (!link) return null;
            const isCurrent = currentPath === link.path;
            const Icon = link.icon;
            return (
              <Link
                key={i}
                to={isCurrent ? "#" : lp(link.path)}
                data-testid={`dv-nav-guide-${i}`}
                className={isCurrent ? "pointer-events-none" : ""}
              >
                <Card className={`border shadow-sm h-full transition-all duration-300 ${isCurrent ? "ring-2 ring-teal-600 bg-teal-50/30" : "hover:shadow-md hover:-translate-y-0.5"} ${link.color}`}>
                  <CardContent className="p-4">
                    <Icon className="h-5 w-5 mb-2" />
                    <h3 className="text-xs font-semibold text-slate-900 leading-tight">{guide.title}</h3>
                    {isCurrent && (
                      <span className="inline-block mt-1.5 text-[10px] font-bold uppercase tracking-wider text-teal-700">{t("dvNav.current")}</span>
                    )}
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Core ImmigrationGuide Tools */}
        <div className="mt-8 pt-6 border-t border-slate-100">
          <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-4 text-center">{t("dvNav.toolsLabel")}</p>
          <div className="flex flex-wrap justify-center gap-2">
            {Array.isArray(tools) && tools.map((tool, i) => {
              const tl = toolLinks[i];
              if (!tl) return null;
              const Icon = tl.icon;
              return (
                <Link key={i} to={lp(tl.path)} data-testid={`dv-nav-tool-${i}`}>
                  <Button variant="outline" size="sm" className="font-medium border-slate-200 text-slate-700 hover:bg-slate-50 text-xs px-3 py-2 h-auto"
                    onClick={() => handleToolClick(i)}>
                    <Icon className="mr-1.5 h-3.5 w-3.5 text-teal-600" /> {tool}
                  </Button>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
